<script setup lang="ts">
import { inject, onBeforeUnmount, watchEffect } from 'vue';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper';
import vtkDataArray from '@kitware/vtk.js/Common/Core/DataArray';
import { VtkViewContext } from '@/src/components/vtk/context';
import { useDentalMeshStore } from './meshStore';
import { columnMajor } from './rigid';
import { observeActor } from './actorObservation';
import { useDentalSceneStore } from './sceneStore';
import { useDentalAppearanceStore } from './appearanceStore';

const view = inject(VtkViewContext);
if (!view) throw new Error('Mesh representation requires a real vtk view.');
const store = useDentalMeshStore();
const scene = useDentalSceneStore();
const appearance = useDentalAppearanceStore();
const mapper = vtkMapper.newInstance();
const actor = vtkActor.newInstance();
actor.setMapper(mapper); actor.getProperty().setOpacity(0.9);
actor.getProperty().setAmbient(0.45); actor.getProperty().setDiffuse(0.55);
view.renderer.addActor(actor);
const removeObservation = observeActor(actor, view.renderer);
watchEffect(() => {
  const poly = store.mesh;
  if (!poly) { actor.setVisibility(false); view.requestRender(); return; }
  void store.changed;
  const rgb = new Uint8Array(poly.getNumberOfPoints() * 4);
  const instances = new Map(store.instances.map(row => [row.labelValue, row]));
  for (let i = 0; i < store.labels.length; i++) {
    const value = store.labels[i];
    const row = instances.get(value);
    // Hiding segmentations leaves the unannotated IOS geometry available.
    const color = row && appearance.visible(row) ? appearance.color(row) : [185, 195, 207, 255];
    rgb.set(color, i * 4);
  }
  poly.getPointData().setScalars(vtkDataArray.newInstance({ name: 'DentalLabelColors', numberOfComponents: 4, values: rgb }));
  mapper.setInputData(poly); mapper.setColorModeToDirectScalars();
  view.requestRender();
});
watchEffect(() => {
  actor.setUserMatrix(Float32Array.from(columnMajor(store.matrix)));
  actor.setVisibility(!!store.mesh && (!scene.enabled || scene.meshVisible));
  actor.getProperty().setOpacity(scene.enabled ? scene.meshOpacity : 0.9);
  view.renderer.resetCameraClippingRange(); view.requestRender();
});
onBeforeUnmount(() => { removeObservation(); view.renderer.removeActor(actor); mapper.delete(); actor.delete(); });
defineExpose({ actor });
</script>

<template><slot /></template>
